import { ApiError } from "../../api/client";
import type { Zone, ZoneDeleted, ZoneInUse } from "../../api/types";
import { Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Presence } from "../../components/presence";
import { t, tServer } from "../../i18n/i18n";
import { useZoneActions } from "../../map/zones/useZones";
import { plural } from "../../plural";
import { type Notice, useUi } from "../../state/ui";
import { showMissionField } from "../openMissionData";
import { InUseDialog, inUseOf } from "../../sharing/InUseDialog";

/** État de la fenêtre d'après le dernier refus : 409 `ZONE_IN_USE` (dossiers à détacher par la suppression forcée,
 *  ou transmission en attente qui bloque), autre refus (message), ou rien. */
export function deleteDialogState(error: unknown): {
  conflict: ZoneInUse | null;
  blocked: boolean;
  other: string | null;
  confirmLabel: string;
  force: boolean;
} {
  const refused = error instanceof ApiError ? error : null;
  const conflict = refused?.status === 409 && refused.code === "ZONE_IN_USE" ? (refused.detail as unknown as ZoneInUse) : null;
  const blocked = Boolean(conflict && (conflict.transfers.length > 0 || (conflict.hidden_transfers ?? 0) > 0));
  return {
    conflict,
    blocked,
    other: refused && !conflict ? refused.message : null,
    confirmLabel: blocked ? t("Fermer") : conflict ? t("Supprimer et détacher les dossiers") : t("Supprimer le terrain"),
    force: Boolean(conflict),
  };
}

/** Début de l'avis serveur posé sur un dossier détaché (`zones/queue.py`, `DETACHED`). */
const DETACHED_PREFIX = "Terrain supprimé :";

/** Avis de la suppression : la zone, les dossiers détachés (nombre et noms) ; si le dossier ouvert en est, le dire,
 *  avec ses autres avis du serveur (déplacements reportés… ; celui du détachement redirait le titre) et l'action
 *  attendue, choisir un camp ou une zone dans les Données initiales (R12). */

export function zoneDeletedNotice(name: string, result: ZoneDeleted, openId: string | null): Omit<Notice, "id"> {
  const text = t("Terrain supprimé : {name}", { name });
  if (result.detached.length === 0) return { text, tone: "ok" };
  const count = plural(result.detached.length, t("dossier détaché du terrain"), t("dossiers détachés du terrain"));
  const detail = t("{count} : {names}.", { count, names: result.detached.map((d) => d.name).join(", ") });
  const open = result.detached.find((d) => d.id === openId);
  if (!open) return { text, detail, tone: "ok" };
  return {
    text,
    // L'avis serveur du détachement (« Terrain supprimé : le dossier n'a plus de terrain… ») redirait le titre : seuls
    // les autres avis du dossier (déplacements reportés…) suivent la liste.
    detail: [detail, t("Le dossier ouvert en fait partie."), ...open.notices.filter((n) => !n.startsWith(DETACHED_PREFIX)).map((n) => tServer(n))].join(" "),
    tone: "info",
    action: { label: t("Choisir un camp ou un terrain"), run: () => showMissionField("dossier-camp") },
  };
}

/** Suppression d'une zone libre (docs/14 §4.3) : confirmation, puis, si des dossiers la citent (409 `ZONE_IN_USE`),
 *  leur liste et la suppression forcée qui les détache de la zone. Une transmission en attente bloque toujours : la
 *  fenêtre le dit et ne propose que de fermer. */
export function ZoneDeleteDialog({ zone, onClose }: { zone: Zone; onClose: () => void }) {
  const { remove } = useZoneActions(zone);
  const notify = useUi((s) => s.notify);
  const { blocked, force } = deleteDialogState(remove.error);
  // Suppression forcée refusée par ricochet (docs/17 §3.13) : un dossier que le compte ne peut pas modifier emploie le
  // terrain ; la fenêtre le dit (dossiers lisibles nommés, autres comptés).
  const inUse = inUseOf(remove.error);
  if (inUse) return <InUseDialog inUse={inUse} kind="zone" onClose={onClose} />;

  const confirm = () => {
    if (blocked) return onClose();
    remove.mutate(force, {
      onSuccess: (result) => {
        onClose();
        notify(zoneDeletedNotice(zone.name, result, useUi.getState().exerciseId));
      },
    });
  };

  return <ZoneDeleteView name={zone.name} error={remove.error} busy={remove.isPending} onConfirm={confirm} onCancel={onClose} />;
}

/** Fenêtre de suppression d'après le dernier refus (`error`) : texte, liste des dossiers (409) ou message, et
 *  bouton de confirmation accordé (« Supprimer la zone », « Supprimer et détacher les dossiers », « Fermer »). */
export function ZoneDeleteView({
  name,
  error,
  busy,
  onConfirm,
  onCancel,
}: {
  name: string;
  error: unknown;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { conflict, blocked, other, confirmLabel } = deleteDialogState(error);
  return (
    <ConfirmDialog
      title={t("Supprimer le terrain « {name} » ?", { name })}
      confirmLabel={confirmLabel}
      tone={blocked ? "neutral" : "danger"}
      busy={busy}
      busyLabel={t("Suppression du terrain…")}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {t("Le terrain préparé (routes, relief, noms de lieux) est effacé du serveur ; l'espace qu'il occupe est libéré.")}
      <Presence show={Boolean(conflict)}>
        {conflict && (
          <Callout tone={blocked ? "danger" : "warn"} className="mt-2" title={tServer(conflict.message)}>
            <ul className="mt-1 space-y-0.5">
              {conflict.exercises.map((ex) => (
                <li key={ex.id} className="break-words">
                  {ex.name}
                </li>
              ))}
              {(conflict.hidden ?? 0) > 0 && (
                // Comptes nominatifs (docs/17 §3.9) : dossiers d'autres comptes comptés, jamais nommés.
                <li className="break-words">{t("et {n} dossiers d'autres comptes", { n: conflict.hidden ?? 0 })}</li>
              )}
              {conflict.transfers.map((tr) => (
                <li key={tr.id} className="break-words">
                  {t("Transmission en attente : {label}", { label: tr.label })}
                </li>
              ))}
              {(conflict.hidden_transfers ?? 0) > 0 && (
                <li className="break-words">{t("et {n} transmissions en attente de dossiers d'autres comptes", { n: conflict.hidden_transfers ?? 0 })}</li>
              )}
            </ul>
          </Callout>
        )}
      </Presence>
      <Presence show={Boolean(other)}>{other && <Callout tone="danger" className="mt-2">{other}</Callout>}</Presence>
    </ConfirmDialog>
  );
}
